import React, { useState, useMemo, useCallback, useImperativeHandle, useRef, useEffect } from 'react';
import Highcharts from 'highcharts/highstock';
import {
  Wind,
  Sun,
  Atom,
  Droplet,
  Flame,
  Leaf,
  Fuel,
  Thermometer,
  Battery,
  ChevronUp,
  ChevronDown,
  Zap,
  Share2,
} from 'lucide-react';
import CapacityInput from '../../CapacityInput';
import { CollapsiblePanelIcon, InfoIcon } from '../../icons';
import { getFuelColor } from '../../../utils/fuelColors';
import VoFuelTechSublist from './VoFuelTechSublist';
import {
  createEmptyInputsLive,
  syncInputsLiveRef,
  getChangedCapacityKeys,
  getChangedDispatchableKeys,
  getChangedVoSubtechTechIds,
  getCapacityInputErrors,
  getDemandInputChanged,
  validateDemandTwhValue,
  canRunUnifiedGenerate,
  sumVoSubtechDraftForFuel,
  VO_SUBTECH_PARENT_FIELD,
  hasExpandableVoSubfuels,
  SUPPLY_WEATHER_YEARS,
  selectInputValueOnFocus,
} from './generationProfileInputsUtils';
import { formatCapacityGwInput } from '../../../utils/numberFormatting';

const dirtyInputClass = 'border-red-400 focus:border-red-500 focus:ring-red-200';
const limitErrorInputClass = 'border-red-500 ring-1 ring-red-300 focus:border-red-600 focus:ring-red-300';
const DECIMAL_2_REGEX = /^\d*(\.\d{0,2})?$/;

function waitForNextPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

const MOBILE_PANEL_HEADER_ROW_CLASS =
  'flex items-center justify-between gap-2 mb-3 min-h-10 py-1';
const MOBILE_PANEL_COLLAPSE_BTN_CLASS = 'btn-panel-toggle-sm';

function draftFromLive(live) {
  return {
    demandTwhInput: live.demandTwhInput ?? '',
    demandTwhError: live.demandTwhError ?? '',
    solarInputGW: live.solarInputGW ?? '',
    windInputGW: live.windInputGW ?? '',
    windOffInputGW: live.windOffInputGW ?? '',
    nuclearInputGW: live.nuclearInputGW ?? '',
    hydroInputGW: live.hydroInputGW ?? '',
    bioInputGW: live.bioInputGW ?? '',
    coalInputGW: live.coalInputGW ?? '',
    geoInputGW: live.geoInputGW ?? '',
    gasInputGW: live.gasInputGW ?? '',
    oilInputGW: live.oilInputGW ?? '',
    storageInputGW: live.storageInputGW ?? '',
    storageDurationInput: live.storageDurationInput ?? '',
    pumpedStorageInputGW: live.pumpedStorageInputGW ?? '',
    pumpedStorageDurationInput: live.pumpedStorageDurationInput ?? '',
    voSubtechByTechId: { ...(live.voSubtechByTechId || {}) },
  };
}

const GenerationProfileInputsPanel = React.memo(
  React.forwardRef(function GenerationProfileInputsPanel(
    {
      inputsLiveRef,
      demandTwhRef,
      leftPanelInputCardRef,
      demandInputAnchorRef,
      lastAppliedDemandTwhInput,
      lastAppliedCapacityInputs,
      lastAppliedDispatchableInputs,
      lastAppliedVoSubtech = null,
      capacityLimits,
      urlSourceInputClass,
      chartData,
      nuclearProfile,
      solarProfile,
      windProfile,
      weatherYear,
      onWeatherYearChange,
      weatherYearsLoading,
      availableWeatherYears,
      weatherYearsError,
      supplyWeatherYear,
      onSupplyWeatherYearChange,
      isDemandGoLoading,
      capacitySubmitLoading,
      dispatchableStorageSubmitLoading,
      onUnifiedGenerate,
      onCopyScenarioLink,
      isCompactViewport,
      onMobileCollapse,
      onDesktopCollapse,
      isDemandGoLoadingDisabled,
      generationRowDefs,
      voCapacityByFuel = null,
    },
    ref
  ) {
    const [draft, setDraft] = useState(() => draftFromLive(inputsLiveRef.current));
    const [vreExpanded, setVreExpanded] = useState(true);
    const [firmCapacityExpanded, setFirmCapacityExpanded] = useState(true);
    /** Parent fuels with sub-techs: expanders start collapsed. */
    const [voFuelExpanded, setVoFuelExpanded] = useState({
      bio: false,
      coal: false,
      gas: false,
      oil: false,
    });
    const [generatePending, setGeneratePending] = useState(false);
    const generateInFlightRef = useRef(false);

    // Keep draft in sync when parent seeds inputsLiveRef before/without a mounted panel ref.
    React.useEffect(() => {
      setDraft((prev) => {
        const live = draftFromLive(inputsLiveRef.current);
        const unchanged =
          prev.demandTwhInput === live.demandTwhInput &&
          prev.solarInputGW === live.solarInputGW &&
          prev.windInputGW === live.windInputGW &&
          prev.windOffInputGW === live.windOffInputGW &&
          prev.nuclearInputGW === live.nuclearInputGW &&
          prev.hydroInputGW === live.hydroInputGW &&
          prev.bioInputGW === live.bioInputGW &&
          prev.coalInputGW === live.coalInputGW &&
          prev.geoInputGW === live.geoInputGW &&
          prev.gasInputGW === live.gasInputGW &&
          prev.oilInputGW === live.oilInputGW &&
          prev.storageInputGW === live.storageInputGW &&
          prev.storageDurationInput === live.storageDurationInput &&
          prev.pumpedStorageInputGW === live.pumpedStorageInputGW &&
          prev.pumpedStorageDurationInput === live.pumpedStorageDurationInput &&
          JSON.stringify(prev.voSubtechByTechId || {}) ===
            JSON.stringify(live.voSubtechByTechId || {});
        return unchanged ? prev : live;
      });
    }, [
      chartData,
      lastAppliedCapacityInputs,
      lastAppliedDispatchableInputs,
      lastAppliedVoSubtech,
      inputsLiveRef,
    ]);
    const [hoverTooltipData, setHoverTooltipData] = useState(null);
    const [overlayTop, setOverlayTop] = useState(0);
    const lastHoverTooltipKeyRef = useRef('__none__');
    const overlayTopTimerRef = useRef(null);

    const measureOverlayTop = useCallback(() => {
      const cardEl = leftPanelInputCardRef.current;
      const demandEl = demandInputAnchorRef.current;
      if (!cardEl || !demandEl) return;
      const cardRect = cardEl.getBoundingClientRect();
      const demandRect = demandEl.getBoundingClientRect();
      setOverlayTop(Math.max(0, demandRect.bottom - cardRect.top + 8));
    }, [leftPanelInputCardRef, demandInputAnchorRef]);

    useEffect(() => {
      if (!hoverTooltipData) return undefined;
      measureOverlayTop();
      const cardEl = leftPanelInputCardRef.current;
      const demandEl = demandInputAnchorRef.current;
      if (!cardEl || !demandEl) return undefined;
      const scheduleMeasure = () => {
        measureOverlayTop();
        if (overlayTopTimerRef.current) clearTimeout(overlayTopTimerRef.current);
        overlayTopTimerRef.current = setTimeout(measureOverlayTop, 180);
      };
      scheduleMeasure();
      const ro = new ResizeObserver(scheduleMeasure);
      ro.observe(cardEl);
      ro.observe(demandEl);
      return () => {
        ro.disconnect();
        if (overlayTopTimerRef.current) {
          clearTimeout(overlayTopTimerRef.current);
          overlayTopTimerRef.current = null;
        }
      };
    }, [hoverTooltipData, measureOverlayTop, leftPanelInputCardRef, demandInputAnchorRef]);

    const setHoverTooltip = useCallback(
      (payload) => {
        const key = payload ? String(payload.x) : '__none__';
        if (lastHoverTooltipKeyRef.current === key) return;
        lastHoverTooltipKeyRef.current = key;
        setHoverTooltipData(payload);
      },
      []
    );

    const applySeed = useCallback(
      (patch) => {
        setDraft((prev) => {
          const next = { ...prev, ...patch };
          syncInputsLiveRef(inputsLiveRef, next);
          if (patch.demandTwhInput !== undefined && demandTwhRef) {
            demandTwhRef.current = patch.demandTwhInput;
          }
          return next;
        });
      },
      [inputsLiveRef, demandTwhRef]
    );

    const resetAll = useCallback(() => {
      const empty = createEmptyInputsLive();
      setDraft(empty);
      syncInputsLiveRef(inputsLiveRef, empty);
      if (demandTwhRef) demandTwhRef.current = '';
      lastHoverTooltipKeyRef.current = '__none__';
      setHoverTooltipData(null);
    }, [inputsLiveRef, demandTwhRef]);

    useImperativeHandle(
      ref,
      () => ({ applySeed, resetAll, setHoverTooltip }),
      [applySeed, resetAll, setHoverTooltip]
    );

    const hoverTooltipMirror = useMemo(() => {
      if (!hoverTooltipData) return null;
      const gap = hoverTooltipData.gap;
      const gapColor = gap > 0 ? '#EF4444' : '#10B981';
      const gapLabel = gap > 0 ? 'Gap (Additional needed)' : 'Surplus';
      const renderValue = (value) =>
        typeof value === 'number' && Number.isFinite(value)
          ? `${Highcharts.numberFormat(value, 2)} GW`
          : '-';
      const fixedGenerationRows = (generationRowDefs || []).map(({ label, colorKey, fallback }) => {
        const existingRow = hoverTooltipData.generationRows?.find((row) => row.label === label);
        const c = getFuelColor(colorKey);
        return {
          label,
          value: existingRow?.value ?? null,
          color: c && c !== '#7F8C8D' ? c : fallback || '#7F8C8D',
        };
      });

      return (
        <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(15,23,42,0.03),0_0_18px_rgba(15,23,42,0.11),0_10px_24px_rgba(15,23,42,0.12)] ring-1 ring-slate-200/80 p-3 sm:p-3 transition-opacity">
          <div className="flex items-center justify-between mb-3">
            <h3 className="card-header-figma">{hoverTooltipData.date}</h3>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Demand</span>
              <span className="font-semibold" style={{ color: '#6366F1' }}>
                {renderValue(hoverTooltipData.demand)}
              </span>
            </div>
            {fixedGenerationRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between">
                <span className="text-gray-600 truncate pr-2">{row.label}</span>
                <span className="font-semibold" style={{ color: row.color }}>
                  {renderValue(row.value)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Battery Charge</span>
              <span className="font-semibold" style={{ color: '#D8BFD8' }}>
                {renderValue(hoverTooltipData.storageCharge)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Pumped Charge</span>
              <span className="font-semibold" style={{ color: '#5DADE2' }}>
                {renderValue(hoverTooltipData.pumpedStorageCharge)}
              </span>
            </div>
            <div className="border-t border-gray-200 pt-2 mt-2 flex items-center justify-between">
              <span className="font-semibold" style={{ color: gap != null ? gapColor : '#6B7280' }}>
                {gap != null ? gapLabel : 'Gap (Additional needed)'}
              </span>
              <span className="font-semibold" style={{ color: gap != null ? gapColor : '#6B7280' }}>
                {gap != null ? `${Highcharts.numberFormat(Math.abs(gap), 2)} GW` : '-'}
              </span>
            </div>
          </div>
        </div>
      );
    }, [generationRowDefs, hoverTooltipData]);

    const handleDemandTwhChange = useCallback(
      (e) => {
        const { value, error } = validateDemandTwhValue(e.target.value);
        setDraft((prev) => {
          const next = { ...prev, demandTwhInput: value, demandTwhError: error };
          syncInputsLiveRef(inputsLiveRef, next);
          if (demandTwhRef) demandTwhRef.current = value;
          return next;
        });
      },
      [inputsLiveRef, demandTwhRef]
    );

    const setField = useCallback(
      (field, value) => {
        setDraft((prev) => {
          const next = { ...prev, [field]: value };
          syncInputsLiveRef(inputsLiveRef, next);
          return next;
        });
      },
      [inputsLiveRef]
    );

    /** Parent fuel GW edit; if exactly one catalog child, keep that tech draft in sync. */
    const setParentFuelField = useCallback(
      (fuel, field, value) => {
        setDraft((prev) => {
          const rows = voCapacityByFuel?.[fuel] || [];
          let next = { ...prev, [field]: value };
          if (rows.length === 1 && rows[0]?.techId) {
            next = {
              ...next,
              voSubtechByTechId: {
                ...(prev.voSubtechByTechId || {}),
                [rows[0].techId]: value,
              },
            };
          }
          syncInputsLiveRef(inputsLiveRef, next);
          return next;
        });
      },
      [inputsLiveRef, voCapacityByFuel]
    );

    const setVoSubtechField = useCallback(
      (fuel, techId, value) => {
        setDraft((prev) => {
          const nextMap = { ...(prev.voSubtechByTechId || {}), [techId]: value };
          const parentField = VO_SUBTECH_PARENT_FIELD[fuel];
          const parentSum = sumVoSubtechDraftForFuel(voCapacityByFuel, fuel, nextMap);
          const next = {
            ...prev,
            voSubtechByTechId: nextMap,
            ...(parentField
              ? { [parentField]: formatCapacityGwInput(parentSum) }
              : {}),
          };
          syncInputsLiveRef(inputsLiveRef, next);
          return next;
        });
      },
      [inputsLiveRef, voCapacityByFuel]
    );

    const toggleVoFuelExpanded = useCallback((fuel) => {
      setVoFuelExpanded((prev) => ({ ...prev, [fuel]: !prev[fuel] }));
    }, []);

    const changedCapacityKeySet = useMemo(
      () => new Set(getChangedCapacityKeys(draft, lastAppliedCapacityInputs)),
      [draft, lastAppliedCapacityInputs]
    );
    const changedDispatchableKeySet = useMemo(
      () => new Set(getChangedDispatchableKeys(draft, lastAppliedDispatchableInputs)),
      [draft, lastAppliedDispatchableInputs]
    );
    const changedVoSubtechIdSet = useMemo(
      () => new Set(getChangedVoSubtechTechIds(draft.voSubtechByTechId, lastAppliedVoSubtech)),
      [draft.voSubtechByTechId, lastAppliedVoSubtech]
    );
    const capacityInputErrors = useMemo(
      () => getCapacityInputErrors(draft, capacityLimits),
      [draft, capacityLimits]
    );
    const demandInputChanged = useMemo(
      () => getDemandInputChanged(draft.demandTwhInput, lastAppliedDemandTwhInput),
      [draft.demandTwhInput, lastAppliedDemandTwhInput]
    );

    const generateState = useMemo(
      () =>
        canRunUnifiedGenerate({
          live: draft,
          lastAppliedDemandTwhInput,
          lastAppliedCapacityInputs,
          lastAppliedDispatchableInputs,
          lastAppliedVoSubtech,
          capacityLimits,
          chartData,
          nuclearProfile,
          solarProfile,
          windProfile,
        }),
      [
        draft,
        lastAppliedDemandTwhInput,
        lastAppliedCapacityInputs,
        lastAppliedDispatchableInputs,
        lastAppliedVoSubtech,
        capacityLimits,
        chartData,
        nuclearProfile,
        solarProfile,
        windProfile,
      ]
    );

    const generateDisabled =
      !generateState.anyRunnable ||
      generateState.capacityLimitsExceeded ||
      generatePending ||
      isDemandGoLoading ||
      capacitySubmitLoading ||
      dispatchableStorageSubmitLoading;

    const handleGenerateClick = useCallback(async () => {
      if (
        generateInFlightRef.current ||
        !generateState.anyRunnable ||
        generateState.capacityLimitsExceeded ||
        isDemandGoLoading ||
        capacitySubmitLoading ||
        dispatchableStorageSubmitLoading
      ) {
        return;
      }
      generateInFlightRef.current = true;
      setGeneratePending(true);
      await waitForNextPaint();
      try {
        await onUnifiedGenerate?.();
      } finally {
        generateInFlightRef.current = false;
        setGeneratePending(false);
      }
    }, [
      generateState.anyRunnable,
      generateState.capacityLimitsExceeded,
      isDemandGoLoading,
      capacitySubmitLoading,
      dispatchableStorageSubmitLoading,
      onUnifiedGenerate,
    ]);

    const submitGenerateFromInput = useCallback(() => {
      if (generateDisabled) return;
      handleGenerateClick();
    }, [generateDisabled, handleGenerateClick]);

    const handleGenerateOnEnterKey = useCallback(
      (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        submitGenerateFromInput();
      },
      [submitGenerateFromInput]
    );

    const hydroDisabled = !chartData || !nuclearProfile || !solarProfile || !windProfile;

    const sourceClass = (scenarioKey, extraClass = '') =>
      urlSourceInputClass ? urlSourceInputClass(scenarioKey, extraClass) : extraClass;

    const renewablesProfileHint = 'Weather year used for Solar, Wind and Hydro profiles.';
    const demandSelectDisabled = weatherYearsLoading || availableWeatherYears.length === 0;
    const demandSelectTitle = weatherYearsLoading
      ? 'Loading weather years...'
      : availableWeatherYears.length === 0
        ? 'No data available'
        : 'Select weather year for demand profile';

    return (
      <div
        ref={leftPanelInputCardRef}
        className="relative bg-white rounded-lg shadow-card p-3 md:p-2 lg:p-4 hover:shadow-card-hover transition-shadow w-full min-w-0"
      >
        <div className={isCompactViewport ? MOBILE_PANEL_HEADER_ROW_CLASS : 'hidden'}>
          <span className="flex-1" aria-hidden />
          <button
            type="button"
            onClick={onMobileCollapse}
            className={MOBILE_PANEL_COLLAPSE_BTN_CLASS}
            title="Collapse panel"
            aria-label="Collapse panel"
          >
            <CollapsiblePanelIcon size={18} className="rotate-180" />
          </button>
        </div>

        <div className="space-y-2">
          <div ref={demandInputAnchorRef} className="flex items-center justify-between gap-[10px]">
            <label className="label-figma whitespace-nowrap">Demand (TWh)</label>
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="number"
                value={draft.demandTwhInput}
                onChange={handleDemandTwhChange}
                onFocus={selectInputValueOnFocus}
                onKeyDown={handleGenerateOnEnterKey}
                className={sourceClass(
                  'demandTwh',
                  `input-field-figma [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${draft.demandTwhError ? 'border-red-500' : demandInputChanged ? dirtyInputClass : ''}`
                )}
                placeholder="0000.00"
                min="0.1"
                max="99999"
                step="0.01"
                disabled={isDemandGoLoadingDisabled}
              />
              {!isCompactViewport ? (
                <button
                  type="button"
                  onClick={onDesktopCollapse}
                  className="btn-panel-toggle-sm"
                  title="Collapse panel"
                  aria-label="Collapse panel"
                >
                  <CollapsiblePanelIcon size={18} className="rotate-180" />
                </button>
              ) : null}
            </div>
          </div>
          {draft.demandTwhError && (
            <p className="text-xs text-red-600 mt-1">{draft.demandTwhError}</p>
          )}
        </div>

        <div className="border-t border-gray-200 my-3" />

        <div>
          <div className="flex items-center justify-between">
            <h3 className="card-header-figma">Variable renewables</h3>
            <button
              type="button"
              onClick={() => setVreExpanded((v) => !v)}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center"
              aria-label={vreExpanded ? 'Collapse' : 'Expand'}
            >
              {vreExpanded ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-in-out"
            style={{ gridTemplateRows: vreExpanded ? '1fr' : '0fr' }}
          >
            <div className="min-h-0 overflow-hidden pt-2 pr-3" aria-hidden={!vreExpanded}>
              <div className="space-y-2">
                <div>
                  <CapacityInput
                    label="Solar (GW)"
                    icon={Sun}
                    iconColor={getFuelColor('solar')}
                    value={draft.solarInputGW}
                    onChange={(v) => setField('solarInputGW', v)}
                    inputClassName={sourceClass(
                      'solarGw',
                      capacityInputErrors.solar
                        ? limitErrorInputClass
                        : changedCapacityKeySet.has('solar')
                          ? dirtyInputClass
                          : ''
                    )}
                    showGoButton={false}
                    onEnter={submitGenerateFromInput}
                  />
                  {capacityInputErrors.solar && (
                    <p className="mt-1 text-xs text-red-600 leading-snug" role="alert">
                      {capacityInputErrors.solar}
                    </p>
                  )}
                </div>
                <div>
                  <CapacityInput
                    label="Wind onshore (GW)"
                    icon={Wind}
                    iconColor={getFuelColor('windon')}
                    value={draft.windInputGW}
                    onChange={(v) => setField('windInputGW', v)}
                    inputClassName={sourceClass(
                      'windOnGw',
                      capacityInputErrors.wind
                        ? limitErrorInputClass
                        : changedCapacityKeySet.has('wind')
                          ? dirtyInputClass
                          : ''
                    )}
                    showGoButton={false}
                    onEnter={submitGenerateFromInput}
                  />
                  {capacityInputErrors.wind && (
                    <p className="mt-1 text-xs text-red-600 leading-snug" role="alert">
                      {capacityInputErrors.wind}
                    </p>
                  )}
                </div>
                <div>
                  <CapacityInput
                    label="Wind offshore (GW)"
                    icon={Wind}
                    iconColor={getFuelColor('windoff')}
                    value={draft.windOffInputGW}
                    onChange={(v) => setField('windOffInputGW', v)}
                    inputClassName={sourceClass(
                      'windOffGw',
                      capacityInputErrors.windOff
                        ? limitErrorInputClass
                        : changedCapacityKeySet.has('windOff')
                          ? dirtyInputClass
                          : ''
                    )}
                    showGoButton={false}
                    onEnter={submitGenerateFromInput}
                  />
                  {capacityInputErrors.windOff && (
                    <p className="mt-1 text-xs text-red-600 leading-snug" role="alert">
                      {capacityInputErrors.windOff}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 my-3" />

        <div>
          <div className="flex items-center justify-between">
            <h3 className="card-header-figma">Firm capacity</h3>
            <button
              type="button"
              onClick={() => setFirmCapacityExpanded((v) => !v)}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center"
              aria-label={firmCapacityExpanded ? 'Collapse' : 'Expand'}
            >
              {firmCapacityExpanded ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-in-out"
            style={{ gridTemplateRows: firmCapacityExpanded ? '1fr' : '0fr' }}
          >
            <div className="min-h-0 overflow-hidden pt-2 pr-3" aria-hidden={!firmCapacityExpanded}>
              <div className="space-y-2">
                <CapacityInput
                  label="Nuclear (GW)"
                  icon={Atom}
                  iconColor={getFuelColor('nuclear')}
                  value={draft.nuclearInputGW}
                  onChange={(v) => setField('nuclearInputGW', v)}
                  inputClassName={sourceClass(
                    'nuclearGw',
                    changedCapacityKeySet.has('nuclear') ? dirtyInputClass : ''
                  )}
                  showGoButton={false}
                  onEnter={submitGenerateFromInput}
                />
                <CapacityInput
                  label="Hydro (GW)"
                  icon={Droplet}
                  iconColor={getFuelColor('hydro')}
                  value={draft.hydroInputGW}
                  onChange={(v) => setField('hydroInputGW', v)}
                  inputClassName={sourceClass(
                    'hydroGw',
                    changedCapacityKeySet.has('hydro') ? dirtyInputClass : ''
                  )}
                  showGoButton={false}
                  disabled={hydroDisabled}
                  onEnter={submitGenerateFromInput}
                />
                <CapacityInput
                  label="Bioenergy (GW)"
                  icon={Leaf}
                  iconColor={getFuelColor('bioenergy') || '#2ECC71'}
                  value={draft.bioInputGW}
                  onChange={(v) => setParentFuelField('bio', 'bioInputGW', v)}
                  inputClassName={sourceClass('bioGw', changedDispatchableKeySet.has('bio') ? dirtyInputClass : '')}
                  showGoButton={false}
                  min="0"
                  readOnly={hasExpandableVoSubfuels(voCapacityByFuel?.bio)}
                  labelPrefix={
                    hasExpandableVoSubfuels(voCapacityByFuel?.bio) ? (
                      <button
                        type="button"
                        onClick={() => toggleVoFuelExpanded('bio')}
                        className="p-0.5 -ml-0.5 rounded hover:bg-gray-100 flex-shrink-0"
                        aria-expanded={voFuelExpanded.bio}
                        aria-label={voFuelExpanded.bio ? 'Collapse bioenergy sub-fuels' : 'Expand bioenergy sub-fuels'}
                      >
                        {voFuelExpanded.bio ? (
                          <ChevronUp className="h-3.5 w-3.5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                        )}
                      </button>
                    ) : null
                  }
                  onEnter={submitGenerateFromInput}
                />
                {hasExpandableVoSubfuels(voCapacityByFuel?.bio) && voFuelExpanded.bio ? (
                  <VoFuelTechSublist
                    rows={voCapacityByFuel?.bio}
                    mode="capacity"
                    editable
                    draftByTechId={draft.voSubtechByTechId}
                    dirtyTechIds={changedVoSubtechIdSet}
                    onChangeTech={(techId, value) => setVoSubtechField('bio', techId, value)}
                    onEnter={submitGenerateFromInput}
                  />
                ) : null}
                <CapacityInput
                  label="Geothermal (GW)"
                  icon={Thermometer}
                  iconColor={getFuelColor('geothermal') || '#E74C3C'}
                  value={draft.geoInputGW}
                  onChange={(v) => setField('geoInputGW', v)}
                  inputClassName={sourceClass('geoGw', changedDispatchableKeySet.has('geo') ? dirtyInputClass : '')}
                  showGoButton={false}
                  min="0"
                  onEnter={submitGenerateFromInput}
                />
                <CapacityInput
                  label="Coal (GW)"
                  icon={Flame}
                  iconColor={getFuelColor('coal') || '#555'}
                  value={draft.coalInputGW}
                  onChange={(v) => setParentFuelField('coal', 'coalInputGW', v)}
                  inputClassName={sourceClass('coalGw', changedDispatchableKeySet.has('coal') ? dirtyInputClass : '')}
                  showGoButton={false}
                  min="0"
                  readOnly={hasExpandableVoSubfuels(voCapacityByFuel?.coal)}
                  labelPrefix={
                    hasExpandableVoSubfuels(voCapacityByFuel?.coal) ? (
                      <button
                        type="button"
                        onClick={() => toggleVoFuelExpanded('coal')}
                        className="p-0.5 -ml-0.5 rounded hover:bg-gray-100 flex-shrink-0"
                        aria-expanded={voFuelExpanded.coal}
                        aria-label={voFuelExpanded.coal ? 'Collapse coal sub-fuels' : 'Expand coal sub-fuels'}
                      >
                        {voFuelExpanded.coal ? (
                          <ChevronUp className="h-3.5 w-3.5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                        )}
                      </button>
                    ) : null
                  }
                  onEnter={submitGenerateFromInput}
                />
                {hasExpandableVoSubfuels(voCapacityByFuel?.coal) && voFuelExpanded.coal ? (
                  <VoFuelTechSublist
                    rows={voCapacityByFuel?.coal}
                    mode="capacity"
                    editable
                    draftByTechId={draft.voSubtechByTechId}
                    dirtyTechIds={changedVoSubtechIdSet}
                    onChangeTech={(techId, value) => setVoSubtechField('coal', techId, value)}
                    onEnter={submitGenerateFromInput}
                  />
                ) : null}
                <CapacityInput
                  label="Gas (GW)"
                  icon={Flame}
                  iconColor={getFuelColor('gas') || '#F39C12'}
                  value={draft.gasInputGW}
                  onChange={(v) => setParentFuelField('gas', 'gasInputGW', v)}
                  inputClassName={sourceClass('gasGw', changedDispatchableKeySet.has('gas') ? dirtyInputClass : '')}
                  showGoButton={false}
                  min="0"
                  readOnly={hasExpandableVoSubfuels(voCapacityByFuel?.gas)}
                  labelPrefix={
                    hasExpandableVoSubfuels(voCapacityByFuel?.gas) ? (
                      <button
                        type="button"
                        onClick={() => toggleVoFuelExpanded('gas')}
                        className="p-0.5 -ml-0.5 rounded hover:bg-gray-100 flex-shrink-0"
                        aria-expanded={voFuelExpanded.gas}
                        aria-label={voFuelExpanded.gas ? 'Collapse gas sub-fuels' : 'Expand gas sub-fuels'}
                      >
                        {voFuelExpanded.gas ? (
                          <ChevronUp className="h-3.5 w-3.5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                        )}
                      </button>
                    ) : null
                  }
                  onEnter={submitGenerateFromInput}
                />
                {hasExpandableVoSubfuels(voCapacityByFuel?.gas) && voFuelExpanded.gas ? (
                  <VoFuelTechSublist
                    rows={voCapacityByFuel?.gas}
                    mode="capacity"
                    editable
                    draftByTechId={draft.voSubtechByTechId}
                    dirtyTechIds={changedVoSubtechIdSet}
                    onChangeTech={(techId, value) => setVoSubtechField('gas', techId, value)}
                    onEnter={submitGenerateFromInput}
                  />
                ) : null}
                <CapacityInput
                  label="Oil (GW)"
                  icon={Fuel}
                  iconColor={getFuelColor('oil') || '#8B4513'}
                  value={draft.oilInputGW}
                  onChange={(v) => setParentFuelField('oil', 'oilInputGW', v)}
                  inputClassName={sourceClass('oilGw', changedDispatchableKeySet.has('oil') ? dirtyInputClass : '')}
                  showGoButton={false}
                  min="0"
                  readOnly={hasExpandableVoSubfuels(voCapacityByFuel?.oil)}
                  labelPrefix={
                    hasExpandableVoSubfuels(voCapacityByFuel?.oil) ? (
                      <button
                        type="button"
                        onClick={() => toggleVoFuelExpanded('oil')}
                        className="p-0.5 -ml-0.5 rounded hover:bg-gray-100 flex-shrink-0"
                        aria-expanded={voFuelExpanded.oil}
                        aria-label={voFuelExpanded.oil ? 'Collapse oil sub-fuels' : 'Expand oil sub-fuels'}
                      >
                        {voFuelExpanded.oil ? (
                          <ChevronUp className="h-3.5 w-3.5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                        )}
                      </button>
                    ) : null
                  }
                  onEnter={submitGenerateFromInput}
                />
                {hasExpandableVoSubfuels(voCapacityByFuel?.oil) && voFuelExpanded.oil ? (
                  <VoFuelTechSublist
                    rows={voCapacityByFuel?.oil}
                    mode="capacity"
                    editable
                    draftByTechId={draft.voSubtechByTechId}
                    dirtyTechIds={changedVoSubtechIdSet}
                    onChangeTech={(techId, value) => setVoSubtechField('oil', techId, value)}
                    onEnter={submitGenerateFromInput}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 my-3" />

        <div>
          <h3 className="card-header-figma mb-2">Storage</h3>
          <div className="space-y-2 pr-3">
                <div className="flex flex-wrap items-center justify-between gap-x-[10px] gap-y-1">
                  <span className="flex items-center gap-1 whitespace-nowrap shrink-0 min-w-0" title="η = 85% round-trip">
                    <label className="label-figma whitespace-nowrap flex items-center gap-1">
                      <Battery className="h-4 w-4 flex-shrink-0" style={{ color: '#9B59B6' }} />
                      <span>Battery</span>
                    </label>
                    <InfoIcon
                      size={10}
                      color="#64748B"
                      strokeWidth={2}
                      className="shrink-0 cursor-help"
                      ariaLabel="η = 85% round-trip"
                    />
                  </span>
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <div
                      className={sourceClass(
                        'batteryStorageGw',
                        `flex items-center h-[25px] min-w-[68px] w-[68px] rounded-md border border-[#E3E3E3] bg-white box-border overflow-hidden focus-within:outline-none focus-within:ring-2 focus-within:ring-vervestacks-primary focus-within:border-transparent transition-all duration-200 ${
                          changedDispatchableKeySet.has('storage') ? dirtyInputClass : ''
                        }`
                      )}
                    >
                      <input
                        type="number"
                        placeholder="0000.00"
                        min="0"
                        step="0.01"
                        value={draft.storageInputGW}
                        onChange={(e) => {
                          const next = e.target.value;
                          if (next === '' || DECIMAL_2_REGEX.test(next)) setField('storageInputGW', next);
                        }}
                        onFocus={selectInputValueOnFocus}
                        onKeyDown={handleGenerateOnEnterKey}
                        className="flex-1 min-w-0 h-full w-full border-0 bg-transparent py-0 pl-1.5 pr-0.5 text-right text-xs leading-[1.2] text-[#3B3B3B] placeholder:text-gray-400 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="pl-0.5 pr-1.5 text-[10px] font-normal text-gray-400 select-none shrink-0 leading-none">
                        GW
                      </span>
                    </div>
                    <div
                      className={sourceClass(
                        'batteryDurationHours',
                        `flex items-center h-[25px] min-w-[68px] w-[68px] rounded-md border border-[#E3E3E3] bg-white box-border overflow-hidden focus-within:outline-none focus-within:ring-2 focus-within:ring-vervestacks-primary focus-within:border-transparent transition-all duration-200 ${
                          changedDispatchableKeySet.has('duration') ? dirtyInputClass : ''
                        }`
                      )}
                    >
                      <input
                        type="number"
                        min="1"
                        max="24"
                        step="1"
                        value={draft.storageDurationInput}
                        onChange={(e) => setField('storageDurationInput', e.target.value)}
                        onFocus={selectInputValueOnFocus}
                        onKeyDown={handleGenerateOnEnterKey}
                        className="flex-1 min-w-0 h-full w-full border-0 bg-transparent py-0 pl-1.5 pr-0.5 text-right text-xs leading-[1.2] text-[#3B3B3B] placeholder:text-gray-400 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="pl-0.5 pr-1.5 text-[10px] font-normal text-gray-400 select-none shrink-0 leading-none">
                        h
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-x-[10px] gap-y-1">
                  <span className="flex items-center gap-1 whitespace-nowrap shrink-0 min-w-0" title="η = 75% round-trip">
                    <label className="label-figma whitespace-nowrap flex items-center gap-1">
                      <Zap className="h-4 w-4 flex-shrink-0" style={{ color: getFuelColor('pumped_storage') }} />
                      <span>Pumped</span>
                    </label>
                    <InfoIcon
                      size={10}
                      color="#64748B"
                      strokeWidth={2}
                      className="shrink-0 cursor-help"
                      ariaLabel="η = 75% round-trip"
                    />
                  </span>
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <div
                      className={sourceClass(
                        'pumpedStorageGw',
                        `flex items-center h-[25px] min-w-[68px] w-[68px] rounded-md border border-[#E3E3E3] bg-white box-border overflow-hidden focus-within:outline-none focus-within:ring-2 focus-within:ring-vervestacks-primary focus-within:border-transparent transition-all duration-200 ${
                          changedDispatchableKeySet.has('pumpedStorage') ? dirtyInputClass : ''
                        }`
                      )}
                    >
                      <input
                        type="number"
                        placeholder="0000.00"
                        min="0"
                        step="0.01"
                        value={draft.pumpedStorageInputGW}
                        onChange={(e) => {
                          const next = e.target.value;
                          if (next === '' || DECIMAL_2_REGEX.test(next)) setField('pumpedStorageInputGW', next);
                        }}
                        onFocus={selectInputValueOnFocus}
                        onKeyDown={handleGenerateOnEnterKey}
                        className="flex-1 min-w-0 h-full w-full border-0 bg-transparent py-0 pl-1.5 pr-0.5 text-right text-xs leading-[1.2] text-[#3B3B3B] placeholder:text-gray-400 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="pl-0.5 pr-1.5 text-[10px] font-normal text-gray-400 select-none shrink-0 leading-none">
                        GW
                      </span>
                    </div>
                    <div
                      className={sourceClass(
                        'pumpedDurationHours',
                        `flex items-center h-[25px] min-w-[68px] w-[68px] rounded-md border border-[#E3E3E3] bg-white box-border overflow-hidden focus-within:outline-none focus-within:ring-2 focus-within:ring-vervestacks-primary focus-within:border-transparent transition-all duration-200 ${
                          changedDispatchableKeySet.has('pumpedDuration') ? dirtyInputClass : ''
                        }`
                      )}
                    >
                      <input
                        type="number"
                        min="1"
                        max="48"
                        step="1"
                        value={draft.pumpedStorageDurationInput}
                        onChange={(e) => setField('pumpedStorageDurationInput', e.target.value)}
                        onFocus={selectInputValueOnFocus}
                        onKeyDown={handleGenerateOnEnterKey}
                        className="flex-1 min-w-0 h-full w-full border-0 bg-transparent py-0 pl-1.5 pr-0.5 text-right text-xs leading-[1.2] text-[#3B3B3B] placeholder:text-gray-400 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="pl-0.5 pr-1.5 text-[10px] font-normal text-gray-400 select-none shrink-0 leading-none">
                        h
                      </span>
                    </div>
                  </div>
                </div>
          </div>
        </div>

        <div className="border-t border-gray-200 my-3" />

        <div className="mb-3">
          <h3 className="card-header-figma mb-2">Weather profiles</h3>
          <div className="space-y-2 pl-2 md:pl-3">
            <div className="flex items-center justify-between gap-[10px]">
              <label className="label-figma whitespace-nowrap text-slate-600">Demand year</label>
              <select
                name="weatherYear"
                value={weatherYear}
                onChange={onWeatherYearChange}
                className={sourceClass('weatherYear', 'input-field-figma')}
                disabled={demandSelectDisabled}
                aria-disabled={demandSelectDisabled}
                title={demandSelectTitle}
              >
                {weatherYearsLoading ? (
                  <option value="">Loading...</option>
                ) : availableWeatherYears.length === 0 ? (
                  <option value="">No data available</option>
                ) : (
                  availableWeatherYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))
                )}
              </select>
            </div>
            {weatherYearsError && <p className="text-xs text-amber-600">{weatherYearsError}</p>}
            <div className="flex items-center justify-between gap-[10px]">
              <span className="flex items-center gap-1 whitespace-nowrap" title={renewablesProfileHint}>
                <label htmlFor="supplyWeatherYear" className="label-figma whitespace-nowrap text-slate-600">
                  Renewables year
                </label>
                <InfoIcon
                  size={14}
                  color="#64748B"
                  strokeWidth={2}
                  className="shrink-0 cursor-help"
                  ariaLabel={renewablesProfileHint}
                />
              </span>
              <select
                id="supplyWeatherYear"
                name="supplyWeatherYear"
                value={supplyWeatherYear}
                onChange={onSupplyWeatherYearChange}
                className={sourceClass('supplyWeatherYear', 'input-field-figma')}
                title={renewablesProfileHint}
              >
                {SUPPLY_WEATHER_YEARS.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-center items-stretch gap-2 pt-1">
          <button
            type="button"
            onClick={handleGenerateClick}
            disabled={generateDisabled}
            className="btn-generate-figma flex-1 min-w-0"
          >
            {generatePending || isDemandGoLoading || capacitySubmitLoading || dispatchableStorageSubmitLoading ? (
              <span className="flex h-4 w-full items-center justify-center leading-none">
                <svg
                  className="animate-spin h-4 w-4 block"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            ) : (
              'Generate'
            )}
          </button>
          {onCopyScenarioLink ? (
            <button
              type="button"
              onClick={onCopyScenarioLink}
              disabled={generateState.capacityLimitsExceeded}
              className="btn-outline flex shrink-0 items-center justify-center px-2.5 disabled:opacity-50"
              title="Copy shareable URL for this simulation"
              aria-label="Copy shareable URL for this simulation"
            >
              <Share2 className="h-4 w-4 shrink-0" aria-hidden />
            </button>
          ) : null}
        </div>

        <div
          className={`absolute inset-x-0 bottom-0 z-20 transition-opacity duration-150 ${
            hoverTooltipData ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          style={{ top: `${overlayTop}px` }}
          aria-hidden={!hoverTooltipData}
        >
          <div className="absolute inset-0 rounded-b-lg bg-slate-900/[0.035] backdrop-blur-[0.5px]" />
          <div className="relative px-2">{hoverTooltipMirror}</div>
        </div>
      </div>
    );
  })
);

export default GenerationProfileInputsPanel;
